import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Menu, X, ChevronDown, ChevronsLeft, ChevronsRight, UserCheck, BookOpenCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TIP_ETIKET } from "@/components/erp";
import { can } from "@/lib/perms";
import { MODULLER, modulBul } from "@/lib/moduller";
import api from "@/lib/api";
import { GeriBildirim } from "@/components/GeriBildirim";
import { UstMenu } from "@/components/UstMenu";

const soon = [{ label: "Muhasebe / e-Defter", icon: BookOpenCheck }, { label: "İK / PDKS", icon: UserCheck }];

const useModuller = (satici) => {
  const { user } = useAuth();
  return MODULLER.map((m) => ({ ...m, tabs: m.tabs.filter((t) => (!t.admin || user?.role === "admin") && (!t.satici || satici) && (!t.perm || can(user, t.perm))) })).filter((m) => m.tabs.length);
};

const OzetNokta = () => {
  const { pathname } = useLocation();
  const [yeni, setYeni] = useState(false);
  useEffect(() => {
    api.get("/bildirim/gunluk-ozet").then((r) => setYeni(!!r.data && !r.data.okundu)).catch(() => setYeni(false));
  }, [pathname]);
  if (pathname === "/gunluk-ozet") return null;
  return yeni ? <span data-testid="ozet-okunmadi" className="ml-auto w-2 h-2 rounded-full bg-amber-400 shadow-[0_0_8px] shadow-amber-400" /> : null;
};

const Sidebar = ({ moduller, dar, setDar, onNav }) => {
  const { user, ayar, logout } = useAuth();
  const aktif = modulBul(useLocation().pathname);
  return (
    <div className="h-full flex flex-col">
      <div className={`h-16 flex items-center gap-3 ${dar ? "justify-center px-2" : "px-5"} border-b border-white/5`}>
        <img src={"/icon-192.png"} alt="logo" data-testid="brand-logo" className="w-9 h-9 shrink-0 rounded-full object-cover glow" />
        {!dar && <div className="min-w-0">
          <div className="font-head font-bold leading-tight truncate" data-testid="brand-name">{ayar?.kisa_ad || ayar?.unvan || "Dinamik Performans"}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground truncate">{ayar?.sektor_adi || "ERP"}</div>
        </div>}
      </div>
      <nav className="flex-1 px-2.5 py-3 overflow-y-auto erp-scroll space-y-1">
        {moduller.map((m) => (
          <NavLink key={m.key} to={m.tabs[0].to} onClick={onNav} title={dar ? m.title : undefined} data-testid={`nav-${m.key}`}
            className={`flex items-center gap-3 ${dar ? "justify-center px-0" : "px-3"} py-2.5 rounded-lg text-[14px] font-medium transition-colors ${aktif?.key === m.key ? "bg-white/10 text-[var(--g1)]" : "text-foreground/85 hover:text-white hover:bg-white/5"}`}>
            <m.icon className="w-[19px] h-[19px] shrink-0" />{!dar && <span className="truncate">{m.title}</span>}{!dar && m.key === "rapor" && can(user, "ozet:read") && <OzetNokta />}
          </NavLink>
        ))}
        {!dar && <div className="pt-5">
          <div className="px-3 mb-2 text-[10px] tracking-[0.25em] uppercase text-muted-foreground/70">Sıradaki Modüller</div>
          {soon.map((s) => (
            <div key={s.label} className="flex items-center gap-3 px-3 py-2 text-sm text-muted-foreground/50 cursor-not-allowed">
              <s.icon className="w-[18px] h-[18px]" />{s.label}
              <span className="ml-auto text-[9px] uppercase tracking-wider border border-white/10 rounded-full px-2 py-0.5">yakında</span>
            </div>
          ))}
        </div>}
      </nav>
      <div className={`p-3 border-t border-white/5 flex items-center gap-2 ${dar ? "flex-col" : ""}`}>
        {!dar && <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-sm font-semibold">{(user?.name || "?").charAt(0)}</div>}
        {!dar && <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="text-[11px] text-muted-foreground">{TIP_ETIKET[user?.role] || user?.role}</div>
        </div>}
        <button data-testid="logout-btn" onClick={logout} className="p-2 rounded-lg hover:bg-white/10 text-muted-foreground hover:text-white transition-colors" title="Çıkış"><LogOut className="w-4 h-4" /></button>
        {setDar && <button data-testid="menu-daralt" onClick={() => setDar(!dar)} title={dar ? "Menüyü genişlet" : "Menüyü daralt"} className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-muted-foreground hover:text-white transition-colors">
          {dar ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}</button>}
      </div>
    </div>
  );
};

const ModulSekmeler = ({ moduller }) => {
  const { pathname } = useLocation();
  const m = modulBul(pathname);
  const mod = m && moduller.find((x) => x.key === m.key);
  if (!mod || mod.tabs.length < 2) return null;
  const tam = mod.tabs.some((t) => t.to === pathname);
  return (
    <div data-testid="modul-sekmeler" className="flex gap-1 overflow-x-auto erp-scroll px-4 sm:px-8 lg:px-10 border-b border-white/10">
      {mod.tabs.map((t) => (
        <NavLink key={t.to} to={t.to} end data-testid={`modul-sekme-${t.to.slice(1).replace(/\//g, "-") || "ana"}`}
          className={({ isActive }) => `flex items-center gap-2 px-3 py-3 text-[13px] whitespace-nowrap -mb-px border-b-2 transition-colors ${isActive || (!tam && t.to !== "/" && pathname.startsWith(`${t.to}/`)) ? "border-[var(--g1)] text-[var(--g1)] font-semibold bg-white/[0.04]" : "border-transparent text-muted-foreground hover:text-white"}`}>
          <t.icon className="w-4 h-4" />{t.label}
        </NavLink>
      ))}
    </div>
  );
};

export const Layout = ({ children }) => {
  const location = useLocation();
  const { ayar } = useAuth();
  const [open, setOpen] = useState(false);
  const [satici, setSatici] = useState(false);
  const [dar, setDarS] = useState(() => localStorage.getItem("menu_dar") === "1");
  const setDar = (v) => { setDarS(v); localStorage.setItem("menu_dar", v ? "1" : "0"); };
  const [genis, setGenis] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const f = (e) => setGenis(e.matches);
    mq.addEventListener("change", f);
    return () => mq.removeEventListener("change", f);
  }, []);
  useEffect(() => { api.get("/lisans").then((r) => setSatici(!!r.data.satici)).catch(() => setSatici(false)); }, []);
  const moduller = useModuller(satici);
  const ml = dar ? "lg:ml-[76px]" : "lg:ml-[250px]";
  return (
    <div className="min-h-screen app-bg">
      <aside data-testid="sidebar" className={`hidden lg:block ${dar ? "w-[76px]" : "w-[250px]"} fixed inset-y-0 glass !rounded-none !border-y-0 !border-l-0 z-20 transition-[width] duration-200`}>
        <Sidebar moduller={moduller} dar={dar} setDar={setDar} />
      </aside>
      <div className="lg:hidden sticky top-0 z-30 h-14 flex items-center gap-2 px-3 glass !rounded-none">
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="p-2"><Menu className="w-5 h-5" /></button>
        <img src={"/icon-192.png"} alt="logo" className="w-7 h-7 rounded-full object-cover shrink-0" />
        <span className="font-head font-semibold truncate flex-1" data-testid="mobile-brand">{ayar?.kisa_ad || ayar?.unvan || "Dinamik Performans"}</span>{!genis && <UstMenu />}
      </div>
      <div className={`sticky top-14 lg:top-0 z-10 ${ml} glass !rounded-none !border-x-0 !border-t-0 transition-[margin] duration-200`}>
        <header data-testid="ust-bar" className="hidden lg:flex h-16 items-center gap-4 px-10 border-b border-white/5">
          <span className="text-sm font-semibold uppercase tracking-wide truncate inline-flex items-center gap-2" data-testid="ust-firma">{ayar?.unvan || ayar?.kisa_ad || ""}<ChevronDown className="w-4 h-4 text-muted-foreground" /></span>
          <div className="ml-auto">{genis && <UstMenu />}</div>
        </header>
        <ModulSekmeler moduller={moduller} />
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/60" onClick={() => setOpen(false)}>
          <aside className="w-[270px] h-full glass !rounded-none" style={{ background: "var(--bg-hex)" }} onClick={(e) => e.stopPropagation()}>
            <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="absolute top-4 left-[230px] p-2"><X className="w-5 h-5" /></button>
            <Sidebar moduller={moduller} dar={false} onNav={() => setOpen(false)} />
          </aside>
        </div>
      )}
      <motion.main key={location.pathname} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
        className={`${ml} p-4 sm:p-8 lg:p-8 pb-24 sm:pb-24 lg:pb-24 max-w-[1600px] transition-[margin] duration-200`}>
        {children}
      </motion.main>
      {!open && <GeriBildirim />}
    </div>
  );
};
